import React, { useEffect, useState } from "react";
import { Table, Panel, Empty, Badge, useClientSort } from "../AdminUI";
import { adminService } from "../admin.service";
import { AdminSalesRep } from "../admin-types";

interface SalesRepsListProps {
  onSelectRep: (rep: AdminSalesRep) => void;
  refreshKey?: number;
}

export default function SalesRepsList({
  onSelectRep,
  refreshKey,
}: SalesRepsListProps) {
  const [reps, setReps] = useState<AdminSalesRep[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // New Rep Form State
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const activeCodeCount = (rep: AdminSalesRep) =>
    rep.referral_codes?.filter((c) => c.is_active).length || 0;

  const { sorted, sortConfig, onSort } = useClientSort(reps, {
    referral_codes: activeCodeCount,
  });

  const fetchReps = async () => {
    setLoading(true);
    try {
      const data = await adminService.fetchSalesReps();
      setReps(data);
    } catch (error) {
      console.error("Error fetching reps", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReps();
  }, [refreshKey]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName || !newEmail) return;
    try {
      await adminService.createSalesRep({ name: newName, email: newEmail });
      setIsCreating(false);
      setNewName("");
      setNewEmail("");
      fetchReps();
    } catch (error) {
      console.error("Error creating rep", error);
      alert("Error creating sales rep");
    }
  };

  return (
    <div className="col-span-12">
      <Panel
        title="Representantes de Ventas"
        right={
          <button
            onClick={() => setIsCreating(!isCreating)}
            className="action-btn solid small"
          >
            {isCreating ? "Cancelar" : "+ Nuevo Representante"}
          </button>
        }
      >
        {isCreating && (
          <div className="admin-form-panel">
             <h3 className="admin-form-title">Crear Nuevo Representante</h3>
             <form onSubmit={handleCreate} className="admin-form">
                <div className="form-group">
                    <label>Nombre</label>
                    <input
                        type="text"
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        className="input-text"
                        required
                    />
                </div>
                <div className="form-group">
                    <label>Email</label>
                    <input
                        type="email"
                        value={newEmail}
                        onChange={e => setNewEmail(e.target.value)}
                        className="input-text"
                        required
                    />
                </div>
                <button type="submit" className="action-btn solid">Guardar</button>
             </form>
          </div>
        )}

        {loading ? (
          <div className="p-4 text-center">Cargando...</div>
        ) : reps.length === 0 ? (
          <Empty>No hay representantes de ventas.</Empty>
        ) : (
          <Table<AdminSalesRep>
            columns={[
              { key: "name", header: "Nombre", sortable: true },
              { key: "email", header: "Email", sortable: true },
              { 
                  key: "referral_codes", 
                  header: "Códigos Activos",
                  sortable: true,
                  render: activeCodeCount
              },
              { 
                  key: "referral_count", 
                  header: "Referidos", 
                  sortable: true,
                  render: (row) => <Badge>{row.referral_count}</Badge>
              },
              { 
                  key: "created_at", 
                  header: "Creado",
                  sortable: true,
                  render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : "-"
              },
            ]}
            rows={sorted}
            onRowClick={onSelectRep}
            sortConfig={sortConfig}
            onSort={onSort}
          />
        )}
      </Panel>
    </div>
  );
}
